import { BuildingNameType } from '@/enums/building';
import type { storeLangsType } from '@/services/dto';
import type { languageType } from '@/stores';

// Comprehensive language mapping for all supported languages
const LANGUAGE_LABELS_MAP = {
    ko: {
        ko: '한국어',
        en: '영어',
        ja: '일본어',
        'zh-CN': '중국어 간체',
        'zh-TW': '중국어 번체',
    },
    en: {
        ko: 'Korean',
        en: 'English',
        ja: 'Japanese',
        'zh-CN': 'Simplified Chinese',
        'zh-TW': 'Traditional Chinese',
    },
    ja: {
        ko: '韓国語',
        en: '英語',
        ja: '日本語',
        'zh-CN': '中国語（簡体字）',
        'zh-TW': '中国語（繁体字）',
    },
    'zh-CN': {
        ko: '韩语',
        en: '英语',
        ja: '日语',
        'zh-CN': '中文（简体）',
        'zh-TW': '中文（繁体）',
    },
    'zh-TW': {
        ko: '韓語',
        en: '英語',
        ja: '日語',
        'zh-CN': '中文（簡體）',
        'zh-TW': '中文（繁體）',
    },
} as const;

export const AppUtils = {
    /**
     * Get language label based on the active language
     * @param targetLang - The language code to get label for
     * @param activeLang - The currently active language (defaults to 'ko')
     * @returns Language label in the active language
     */
    getLanguageLabelByActiveLang: (targetLang: languageType, activeLang: languageType = 'ko'): string => {
        return LANGUAGE_LABELS_MAP[activeLang][targetLang] || LANGUAGE_LABELS_MAP.ko[targetLang];
    },

    getLanguageLabel: (lang: languageType) => {
        const labels: Record<languageType, string> = {
            ko: '한국어',
            en: 'English',
            'zh-CN': '中文（简体）',
            'zh-TW': '中文（繁体）',
            ja: '日本語',
        };
        return labels[lang];
    },
    getLanguageTTS: (lang: languageType) => {
        const tts: Record<languageType, string> = {
            ko: 'ko-KR',
            en: 'en-US',
            'zh-CN': 'zh-CN',
            'zh-TW': 'zh-TW',
            ja: 'ja-JP',
        };
        return tts[lang];
    },
    getLanguageFont: (lang: languageType, buildingType?: BuildingNameType) => {
        const fonts: Record<languageType, string> = {
            ko: "'Pretendard', sans-serif",
            en: "'Pretendard', sans-serif",
            'zh-CN': "'Noto Sans CJK SC', sans-serif",
            'zh-TW': "'Noto Sans CJK TC', sans-serif",
            ja: "'Pretendard JP', sans-serif",
        };
        return fonts[lang];
    },
    OPTION_LANGUAGE: (langs: storeLangsType[]) => {
        return [...langs]
            .sort((a, b) => {
                const orderA = a.sort_order ?? 0;
                const orderB = b.sort_order ?? 0;
                if (orderA !== orderB) {
                    return orderA - orderB;
                }
                return a.code.localeCompare(b.code);
            })
            .map((lang) => ({
                label: lang.display_name || lang.name || AppUtils.getLanguageLabel(lang.code),
                value: lang.code,
            }));
    },
    getPhoneNumberTTSText: (phone: string) => {
        return phone.replaceAll('-', '').split('').join(', ');
    },
    getFloorTTSText: (floor: string) => {
        return floor.replace(/F/g, '').replace(/f/g, '');
    },
};
